/**
 * Getting an authorization CODE out of the browser, for the server to exchange.
 *
 * EXTRACTED FROM `Settings/calendar/connectFlows.ts`, WHICH IS WHERE THIS WAS
 * LEARNED. That file used to build both authorize URLs itself and three things
 * followed, the first a real defect in production: Google was never asked for
 * offline access and could not be, because the GIS code client exposes no
 * `access_type` and no `prompt`; there was no `state`, so the CSRF defence was
 * a postMessage origin check that says nothing about the round trip; and there
 * was no PKCE. All three need a party that can keep a secret and recognise its
 * own nonce, which the browser is not. So the API says where to send the user
 * and this only opens the window and relays the reply.
 *
 * A SECOND COPY FOR MAIL WOULD BE THE WRONG SHAPE. What differs between a
 * calendar and a mailbox is a path, a window name and which endpoint mints the
 * URL. What is the same is every line that handles a credential, unregisters a
 * listener, or decides that a reply is not the one being waited for — and a
 * copy is how the next fix to those lands in one of them.
 *
 * A code is short-lived, single-use, and worth nothing without the client
 * secret. It is still a credential: never logged, never put in a URL we own.
 */

export interface AuthCode {
  code: string;
  /** The nonce the server minted. It refuses the exchange without it. */
  state: string;
  redirectUri: string;
}

/** What a callback route posts back to the window that opened it. */
export interface CallbackMessage {
  source: string;
  code?: string;
  state?: string;
  error?: string;
}

export function isCallbackMessage(
  source: string,
  data: unknown,
): data is CallbackMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { source?: unknown }).source === source
  );
}

export interface PopupAuthRequest {
  /** The `source` string the callback route stamps on its message. */
  source: string;
  /** Path on this origin the provider redirects back to. */
  callbackPath: string;
  /** Names the popup, so a second click reuses the window rather than opening
   *  a second one behind the first. */
  windowName: string;
  /** Asks the API for the URL to send the user to, and the state it will
   *  recognise on the way back. */
  begin: (
    redirectUri: string,
  ) => Promise<{ authorize_url: string; state: string }>;
  /** What to say when `begin` fails for a reason it did not explain. */
  startFailure: string;
}

export async function popupAuthCode({
  source,
  callbackPath,
  windowName,
  begin,
  startFailure,
}: PopupAuthRequest): Promise<AuthCode> {
  const redirectUri = `${window.location.origin}${callbackPath}`;

  // OPENED BEFORE THE REQUEST COMPLETES, because a `window.open` that is not a
  // direct result of the click is blocked by every browser.
  const popup = window.open('', windowName, 'width=520,height=680');
  if (!popup) {
    throw new Error(
      'The sign-in window was blocked. Allow popups and try again.',
    );
  }

  let authorizeUrl: string;
  let expectedState: string;
  try {
    const started = await begin(redirectUri);
    authorizeUrl = started.authorize_url;
    expectedState = started.state;
  } catch (err) {
    popup.close();
    throw err instanceof Error ? err : new Error(startFailure);
  }

  popup.location.href = authorizeUrl;

  return new Promise<AuthCode>((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      window.clearInterval(closedTimer);
      fn();
    };

    const onMessage = (event: MessageEvent) => {
      // Same-origin only. The callback route is ours, so anything from
      // elsewhere is not the reply we are waiting for, and a code is a
      // credential: a message claiming to carry one is exactly what must not be
      // trusted on origin alone.
      if (event.origin !== window.location.origin) return;
      if (!isCallbackMessage(source, event.data)) return;
      const { code, state, error } = event.data;
      finish(() => {
        popup.close();
        if (error) {
          reject(new Error(error));
          return;
        }
        // Checked here as well as on the server. The server's check is the one
        // that protects the account; this one turns a mismatched reply into a
        // message about the sign-in rather than a confusing 400 from the
        // exchange that follows.
        if (!code || !state || state !== expectedState) {
          reject(new Error('The sign-in reply did not match this request.'));
          return;
        }
        resolve({ code, state, redirectUri });
      });
    };

    // A user who closes the popup has cancelled, and nothing will ever post.
    // Without this the promise hangs and the button spins forever.
    const closedTimer = window.setInterval(() => {
      if (popup.closed) {
        finish(() => reject(new Error('Sign-in was cancelled.')));
      }
    }, 500);

    window.addEventListener('message', onMessage);
  });
}
